import { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, ListChecks, Loader2, Lock, LogOut, QrCode, Search, Undo2, UserPlus, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";

function LoginGate({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      if (!["admin", "staff"].includes(data.role)) {
        setError("This login does not have check-in access.");
        return;
      }
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-5" data-testid="checkin-login">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md rounded-3xl border border-gold/25 bg-ink-card/80 p-8 backdrop-blur-xl sm:p-10"
      >
        <div className="flex flex-col items-center text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10 text-gold">
            <QrCode className="h-8 w-8" />
          </span>
          <h1 className="mt-5 font-display text-2xl font-semibold">Event Check-In</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Staff access — scan attendee QR codes at the door.</p>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label htmlFor="ci-email">Email</Label>
            <Input id="ci-email" data-testid="checkin-email-input" type="email" required autoComplete="email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 border-gold/20 bg-ink" placeholder="checkin@abhunter.us" />
          </div>
          <div>
            <Label htmlFor="ci-password">Password</Label>
            <Input id="ci-password" data-testid="checkin-password-input" type="password" required autoComplete="current-password"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1.5 border-gold/20 bg-ink" placeholder="••••••••" />
          </div>
          {error && (
            <p data-testid="checkin-login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</p>
          )}
          <Button data-testid="checkin-login-submit" type="submit" disabled={loading}
            className="w-full rounded-full bg-gold py-6 font-semibold text-ink transition-all hover:bg-gold-hover">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="mr-2 h-4 w-4" /> Sign In</>}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}

export default function CheckInPage() {
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [events, setEvents] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [tab, setTab] = useState("scan");
  const [attendees, setAttendees] = useState([]);
  const [search, setSearch] = useState("");
  const [result, setResult] = useState(null);
  const [manualCode, setManualCode] = useState("");
  const [camError, setCamError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [walkinOpen, setWalkinOpen] = useState(false);
  const [walkin, setWalkin] = useState({ name: "", email: "", phone: "", attendees: 1 });
  const [walkinSaving, setWalkinSaving] = useState(false);
  const lastScan = useRef({ code: "", at: 0 });

  const event = (events || []).find((e) => e.id === eventId) || null;

  useEffect(() => {
    api.get("/checkin/me")
      .then(({ data }) => setMe(data))
      .catch(() => setMe(null))
      .finally(() => setChecking(false));
  }, []);

  const loadEvents = useCallback(() => {
    api.get("/checkin/events").then(({ data }) => setEvents(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { if (me) loadEvents(); }, [me, loadEvents]);

  const loadAttendees = useCallback(() => {
    if (!eventId) return;
    api.get(`/checkin/events/${eventId}/attendees`).then(({ data }) => setAttendees(data)).catch(() => {});
    loadEvents();
  }, [eventId, loadEvents]);

  useEffect(() => { loadAttendees(); }, [loadAttendees]);

  const handleScan = useCallback(async (raw) => {
    const code = (raw || "").trim();
    if (!code) return;
    const nowMs = Date.now();
    if (code === lastScan.current.code && nowMs - lastScan.current.at < 4000) return;
    lastScan.current = { code, at: nowMs };
    try {
      const { data } = await api.post("/checkin/scan", { code });
      setResult({ kind: data.ok ? "ok" : "already", ...data });
      navigator.vibrate?.(150);
    } catch (err) {
      setResult({ kind: "error", message: formatApiError(err) });
      navigator.vibrate?.([80, 60, 80]);
    }
    loadAttendees();
  }, [loadAttendees]);

  // auto-clear the scan feedback card
  useEffect(() => {
    if (!result) return;
    const t = setTimeout(() => setResult(null), 4000);
    return () => clearTimeout(t);
  }, [result]);

  // camera lifecycle
  useEffect(() => {
    if (!me || !eventId || tab !== "scan") return;
    setCamError("");
    const scanner = new Html5Qrcode("qr-reader");
    let stopped = false;
    scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (text) => handleScan(text),
        () => {}
      )
      .then(() => !stopped && setScanning(true))
      .catch(() => setCamError("Camera unavailable — allow camera access, or use the List tab to check in by name."));
    return () => {
      stopped = true;
      setScanning(false);
      scanner.stop().then(() => scanner.clear()).catch(() => {});
    };
  }, [me, eventId, tab, handleScan]);

  const undo = async (code) => {
    try {
      await api.post("/checkin/undo", { code });
      toast.success("Check-in undone.");
      loadAttendees();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitWalkin = async (e) => {
    e.preventDefault();
    setWalkinSaving(true);
    try {
      const { data } = await api.post(`/checkin/events/${eventId}/walkin`, {
        name: walkin.name, email: walkin.email || null, phone: walkin.phone,
        attendees: Number(walkin.attendees) || 1,
      });
      setResult({ kind: "ok", name: `${data.name} (walk-up)`, party_checked: data.attendees, party_size: data.attendees });
      toast.success(`${data.name} added and checked in.`);
      setWalkin({ name: "", email: "", phone: "", attendees: 1 });
      setWalkinOpen(false);
      loadAttendees();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setWalkinSaving(false);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setMe(null);
    setEventId(null);
  };

  const filtered = attendees.filter((a) =>
    !search || `${a.name} ${a.group} ${a.email}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="checkin-page" className="min-h-screen bg-ink text-foreground">
      <Seo noindex title="Event Check-In — AB Hunter Consulting USA" siteName="AB Hunter Consulting USA" description="Staff check-in portal." />
      {checking ? (
        <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
      ) : !me ? (
        <LoginGate onLogin={(u) => setMe(u)} />
      ) : (
        <main className="mx-auto max-w-2xl px-5 pb-20 pt-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/assets/logo.png" alt="AB Hunter Consulting USA logo" className="h-10 w-auto object-contain" />
              <div>
                <p className="font-display font-semibold leading-tight">Event Check-In</p>
                <p className="text-xs text-muted-foreground">{me.name} · {me.role === "admin" ? "Admin" : "Door staff"}</p>
              </div>
            </div>
            <Button data-testid="checkin-logout" size="sm" variant="outline" onClick={logout}
              className="border-gold/30 bg-transparent text-gold hover:bg-gold/10">
              <LogOut className="mr-1.5 h-3.5 w-3.5" /> Sign out
            </Button>
          </div>

          {!event ? (
            <div className="mt-10" data-testid="checkin-event-picker">
              <h2 className="font-display text-xl font-semibold">Choose the event you're working</h2>
              <p className="mt-1 text-sm text-muted-foreground">Upcoming events with registrations appear here.</p>
              <div className="mt-6 space-y-3">
                {(events || []).map((e) => (
                  <button key={e.id} data-testid={`checkin-event-${e.id}`} onClick={() => setEventId(e.id)}
                    className="w-full rounded-2xl border border-gold/20 bg-ink-card/70 p-5 text-left transition-all hover:border-gold/50 hover:bg-ink-card">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="truncate font-display font-semibold">{e.title}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{e.date_label} · {e.location}</p>
                      </div>
                      <span className="shrink-0 font-mono text-sm text-gold">{e.checked_in}/{e.tickets_total}</span>
                    </div>
                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gold/10">
                      <div className="h-full rounded-full bg-gold transition-[width] duration-500"
                        style={{ width: `${e.tickets_total ? (e.checked_in / e.tickets_total) * 100 : 0}%` }} />
                    </div>
                  </button>
                ))}
                {events && events.length === 0 && (
                  <p className="rounded-xl border border-dashed border-gold/20 py-10 text-center text-sm text-muted-foreground" data-testid="checkin-no-events">
                    No upcoming events right now.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-8" data-testid="checkin-session">
              <button data-testid="checkin-back-to-events" onClick={() => { setEventId(null); setResult(null); }}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-gold">
                <ArrowLeft className="h-4 w-4" /> Switch event
              </button>
              <h2 className="mt-3 font-display text-2xl font-semibold leading-tight">{event.title}</h2>
              <div className="mt-3 flex items-center gap-4">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-gold/10">
                  <div className="h-full rounded-full bg-gold transition-[width] duration-500"
                    style={{ width: `${event.tickets_total ? (event.checked_in / event.tickets_total) * 100 : 0}%` }} />
                </div>
                <span className="font-mono text-sm text-gold" data-testid="checkin-progress">{event.checked_in}/{event.tickets_total} in</span>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-2 rounded-full border border-gold/20 bg-ink-card/60 p-1.5">
                <button data-testid="checkin-tab-scan" onClick={() => setTab("scan")}
                  className={`flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition-all ${tab === "scan" ? "bg-gold text-ink" : "text-muted-foreground hover:text-foreground"}`}>
                  <QrCode className="h-4 w-4" /> Scan
                </button>
                <button data-testid="checkin-tab-list" onClick={() => setTab("list")}
                  className={`flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition-all ${tab === "list" ? "bg-gold text-ink" : "text-muted-foreground hover:text-foreground"}`}>
                  <ListChecks className="h-4 w-4" /> List
                </button>
              </div>

              <button data-testid="checkin-walkin-button" onClick={() => setWalkinOpen(!walkinOpen)}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-gold/40 bg-gold/5 py-3.5 text-sm font-semibold text-gold transition-all hover:border-gold hover:bg-gold/10">
                <UserPlus className="h-4 w-4" /> {walkinOpen ? "Close walk-up form" : "Add walk-up guest"}
              </button>

              {walkinOpen && (
                <motion.form
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  onSubmit={submitWalkin}
                  data-testid="walkin-form"
                  className="mt-4 space-y-4 rounded-2xl border border-gold/25 bg-ink-card/70 p-5"
                >
                  <div>
                    <Label htmlFor="wu-name">Guest name *</Label>
                    <Input id="wu-name" data-testid="walkin-name-input" required value={walkin.name}
                      onChange={(e) => setWalkin({ ...walkin, name: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" placeholder="Full name" />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="wu-email">Email (optional — sends their QR)</Label>
                      <Input id="wu-email" data-testid="walkin-email-input" type="email" value={walkin.email}
                        onChange={(e) => setWalkin({ ...walkin, email: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" placeholder="guest@email.com" />
                    </div>
                    <div>
                      <Label htmlFor="wu-phone">Phone (optional)</Label>
                      <Input id="wu-phone" data-testid="walkin-phone-input" type="tel" value={walkin.phone}
                        onChange={(e) => setWalkin({ ...walkin, phone: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="wu-count">How many people (including them)</Label>
                    <Input id="wu-count" data-testid="walkin-count-input" type="number" min="1" max="10" required value={walkin.attendees}
                      onChange={(e) => setWalkin({ ...walkin, attendees: e.target.value })} className="mt-1.5 border-gold/20 bg-ink" />
                  </div>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Walk-ups are added free of charge and checked in immediately — collect payment separately at the door if needed.
                  </p>
                  <Button data-testid="walkin-submit" type="submit" disabled={walkinSaving}
                    className="w-full rounded-full bg-gold py-5 font-semibold text-ink hover:bg-gold-hover">
                    {walkinSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add & check in"}
                  </Button>
                </motion.form>
              )}

              {tab === "scan" ? (
                <div className="mt-6">
                  <div className="relative overflow-hidden rounded-3xl border border-gold/25 bg-black">
                    <div id="qr-reader" data-testid="checkin-scanner" className="w-full [&_video]:!h-auto [&_video]:!w-full" />
                    <AnimatePresence>
                      {result && (
                        <motion.div
                          key={`${result.kind}-${result.name || result.message}-${lastScan.current.at}`}
                          initial={{ opacity: 0, scale: 0.92 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          data-testid="checkin-result"
                          className={`absolute inset-x-4 bottom-4 rounded-2xl border p-5 text-center backdrop-blur-xl ${
                            result.kind === "ok"
                              ? "border-emerald-400/40 bg-emerald-500/15"
                              : result.kind === "already"
                                ? "border-amber-400/40 bg-amber-500/15"
                                : "border-red-400/40 bg-red-500/15"
                          }`}
                        >
                          {result.kind === "ok" ? (
                            <>
                              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-300" />
                              <p className="mt-2 font-display text-xl font-semibold text-emerald-200">{result.name} — checked in!</p>
                              <p className="mt-1 text-xs text-emerald-200/70">Party: {result.party_checked} of {result.party_size} in</p>
                            </>
                          ) : result.kind === "already" ? (
                            <>
                              <XCircle className="mx-auto h-10 w-10 text-amber-300" />
                              <p className="mt-2 font-display text-xl font-semibold text-amber-200">{result.name} is already in</p>
                              <p className="mt-1 text-xs text-amber-200/70">
                                Checked in {result.checked_in_at ? new Date(result.checked_in_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "earlier"}
                              </p>
                            </>
                          ) : (
                            <>
                              <XCircle className="mx-auto h-10 w-10 text-red-300" />
                              <p className="mt-2 font-display text-lg font-semibold text-red-200">{result.message}</p>
                            </>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  {camError && (
                    <p data-testid="checkin-camera-error" className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-300">{camError}</p>
                  )}
                  {scanning && !result && (
                    <p className="mt-4 text-center text-xs text-muted-foreground">Point the camera at an attendee's QR code — check-in is automatic.</p>
                  )}
                  <form
                    className="mt-5 flex gap-2"
                    onSubmit={(e) => { e.preventDefault(); handleScan(manualCode); setManualCode(""); }}
                  >
                    <Input data-testid="checkin-manual-code" value={manualCode} onChange={(e) => setManualCode(e.target.value)}
                      placeholder="Or enter ticket code manually" className="border-gold/20 bg-ink-card" />
                    <Button data-testid="checkin-manual-submit" type="submit" disabled={!manualCode.trim()}
                      className="shrink-0 rounded-full bg-gold px-6 font-semibold text-ink hover:bg-gold-hover">Check in</Button>
                  </form>
                </div>
              ) : (
                <div className="mt-6">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input data-testid="checkin-search" value={search} onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search name, group, or email…" className="border-gold/20 bg-ink-card pl-10" />
                  </div>
                  <div className="mt-4 space-y-2" data-testid="checkin-attendee-list">
                    {filtered.map((a) => (
                      <div key={a.code} data-testid={`checkin-attendee-${a.code}`}
                        className={`flex items-center gap-3 rounded-xl border p-4 ${a.checked_in ? "border-emerald-400/25 bg-emerald-500/5" : "border-gold/15 bg-ink-card/60"}`}>
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium">{a.name}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {a.group !== a.name ? `${a.group} · ` : ""}{a.email}{a.tier ? ` · ${a.tier}` : ""}
                          </p>
                        </div>
                        {a.checked_in ? (
                          <div className="flex items-center gap-2">
                            <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-300">
                              <CheckCircle2 className="h-4 w-4" />
                              {a.checked_in_at ? new Date(a.checked_in_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "In"}
                            </span>
                            <button data-testid={`checkin-undo-${a.code}`} aria-label={`Undo check-in for ${a.name}`} onClick={() => undo(a.code)} title="Undo check-in"
                              className="rounded-full border border-gold/20 p-1.5 text-muted-foreground transition-colors hover:border-red-400/40 hover:text-red-300">
                              <Undo2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <Button data-testid={`checkin-attendee-button-${a.code}`} size="sm" onClick={() => handleScan(a.code)}
                            className="rounded-full bg-gold font-semibold text-ink hover:bg-gold-hover">Check in</Button>
                        )}
                      </div>
                    ))}
                    {filtered.length === 0 && (
                      <p className="py-10 text-center text-sm text-muted-foreground">No attendees match that search.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      )}
    </div>
  );
}
